import { EventEmitter, Injectable, Output } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '@env';
import { map } from 'rxjs/operators';
// import { compare } from 'fast-json-patch';
// import { flattenObject } from '@utils/utils';
// import { MessageType, MessageTypes } from '@models/sms/messagetype';
import { PagesTemplate } from '@models/pages/templates';
import { UserDomain, UserDomains } from '@models/pages/user-domains';
import { BrandDomains } from '@models/pages/brand-domains';
import { FormInteractions, FormInteraction } from '@models/pages/page';
// import { ApiGetContactsOptions } from '@models/contact/contact';
import { ApiGetFormInteractionsOptions } from '@models/pages/page';

import {
  ApiGetPageOptions,
  Page,
  Pages,
  EditPage,
  PublishPageRequest,
  AtlasPageRequest,
  PageUpTimeStats,
  PageStatsVisitsByDay,
  PageStatsDeviceAndOs,
  PageStatsClicksByDay
} from '@models/pages/page';
import Hashids from 'hashids';
import { TranslateService } from '@ngx-translate/core';
import { AtlasToastService } from './atlas-toast.service';
import { ToastContainerDirective } from 'ngx-toastr';

@Injectable({
  providedIn: 'root'
})
export class PagesService {
  private readonly baseUrl;
  private removeTagHtmlTitlePlaceholder = '%HTML_TITLE_PLACEHOLDER%';

  @Output() selectionChange: EventEmitter<any> = new EventEmitter();

  constructor(private http: HttpClient, private translateService: TranslateService, private atlasToast: AtlasToastService) {
    this.baseUrl = environment.serverBaseUrl;
  }

  getPagesTemplateList(): Observable<Array<PagesTemplate>> {
    return this.http
      .get<Array<PagesTemplate>>(this.baseUrl + 'pages/templates')
      .pipe(map(templates => templates.map(template => PagesTemplate.deserialize(template))));
  }

  getBrandDomains(): Observable<Array<BrandDomains>> {
    return this.http
      .get<Array<BrandDomains>>(this.baseUrl + 'pages/domains/brand')
      .pipe(map(brandDomains => brandDomains.map(brandDomain => BrandDomains.deserialize(brandDomain))));
  }

  getUserDomains(): Observable<UserDomains> {
    return this.http.get<Array<UserDomains>>(this.baseUrl + 'pages/domains/user').pipe(
      map(data => {
        return UserDomains.deserialize({
          data: (data ? ((data as unknown) as Array<string>) : []).map(data2 => UserDomains.deserialize(data2))
        });
      })
    );
  }

  addDomain(userDomain: UserDomain) {
    return this.http.post(this.baseUrl + 'pages/domains/user', userDomain, { responseType: 'text' as 'json' });
  }

  subscribeTrial(): Observable<any | null> {
    return this.http.post(this.baseUrl + 'pages/activate-trial', {}, { responseType: 'text' as 'json' });
  }

  canPagesTrial() {
    return this.http.get(this.baseUrl + 'pages/activate-trial', {});
  }

  getPageById(idPage: number): Observable<Page> {
    return this.http.get<Response>(this.baseUrl + 'pages/single-page/' + idPage).pipe(map(page => Page.deserialize(page)));
  }

  getPages(page?, size?, sortColumn?, sortDirection?, body?: ApiGetPageOptions): Observable<Pages> {
    let params = new HttpParams();
    if (page) {
      params = params.append('page', page);
    }
    if (size) {
      params = params.append('size', size);
    }
    if (sortColumn) {
      params = params.append('sortColumn', sortColumn);
    } else {
      params = params.append('sortColumn', 'ID_PAGE');
    }
    if (sortDirection) {
      params = params.append('sortDirection', sortDirection);
    } else {
      params = params.append('sortDirection', 'd');
    }
    return this.http
      .post(this.baseUrl + 'pages/list-pages', body, {
        params
      })
      .pipe(
        map(data => {
          return Pages.deserialize({
            data: (data && (data as any).data ? ((data as any).data as Array<string>) : []).map(data2 => Page.deserialize(data2)),
            total: (data as any).total
          });
        })
      );
  }

  getPageShortLinkPlaceholder(): Observable<string> {
    return this.http.get<string>(this.baseUrl + 'pages/get-shortlink-placeholder');
  }

  publishPage(requestBody: PublishPageRequest) {
    return this.http.post(this.baseUrl + 'pages/publish-page', requestBody);
  }

  unPublishPage(idPage: number) {
    const urlApi: string = 'pages/' + idPage + '/unpublish-page';
    return this.http.post(this.baseUrl + urlApi, null);
  }

  deletePage(idPage: number) {
    const urlApi: string = 'pages/' + idPage;
    return this.http.delete(this.baseUrl + urlApi);
  }

  archivePage(idPage: number) {
    const urlApi: string = 'pages/' + idPage + '/archive-page';
    return this.http.post(this.baseUrl + urlApi, null);
  }

  createPage(description): Observable<any> {
    return this.http.post(this.baseUrl + 'pages', { description });
  }

  getPage(idPage): Observable<Page> {
    return this.http.get<Page>(this.baseUrl + 'pages/single-page/' + idPage);
  }

  editPage(data: EditPage) {
    return this.http.put(this.baseUrl + 'pages/edit-page', data, {
      responseType: 'text' as 'json'
    });
  }

  editorPageCompose(idPage: number, hashids: Hashids) {
    window.location.href = '/pages/compose?page=' + hashids.encode(idPage);
  }

  copyPage(request: AtlasPageRequest): Observable<any> {
    const urlApi = 'pages/copy-page';
    return this.http.post(this.baseUrl + urlApi, request);
  }

  editPublishedPage(request: AtlasPageRequest): Observable<any> {
    const urlApi = 'pages/edit-published-page';
    return this.http.post(this.baseUrl + urlApi, request);
  }

  buildAtlasPageRequest(page: Page, isCopyAction: boolean) {
    const request = new AtlasPageRequest();
    request.idPage = page.idPage;
    if (isCopyAction) {
      request.description = this.getTranslateLabels('pages_default_copied_title') + page.description;
    } else {
      request.description = page.description;
    }
    request.idUserDomain = page.idUserDomain;
    return request;
  }

  buildPublishPageRequest(page?: Page): PublishPageRequest {
    const request: PublishPageRequest = new PublishPageRequest();
    request.idUserDomain = page.idUserDomain;
    request.name = page.name;
    request.idPage = page.idPage;
    return request;
  }

  buildRequestPublishPageByStatusDraft(data: any, idPage: number): PublishPageRequest {
    const request: PublishPageRequest = new PublishPageRequest();
    request.idUserDomain = data.idUserDomain;
    request.name = data.name;
    request.idPage = idPage;
    return request;
  }

  private getTranslateLabels(value: string) {
    if (value) {
      let labels;
      this.translateService.get(value).subscribe(label => {
        labels = label;
      });
      return labels;
    }
  }

  getRemoveTagFromPreview() {
    return this.removeTagHtmlTitlePlaceholder;
  }

  getPagesStatsGetUpTime(idPage: number): Observable<PageUpTimeStats> {
    return this.http.get<PageUpTimeStats>(this.baseUrl + 'pages/' + idPage + '/stats/get-page-uptime');
  }

  getPagesStatsGetVisitsByDay(idPage, from, to): Observable<PageStatsVisitsByDay> {
    const params = new HttpParams().set('from', from).set('to', to);
    return this.http.get<PageStatsVisitsByDay>(this.baseUrl + 'pages/' + idPage + '/stats/get-visits-by-day', { params });
  }

  getPagesStatsGetDeviceAndOs(idPage, from, to): Observable<PageStatsDeviceAndOs> {
    const params = new HttpParams().set('from', from).set('to', to);
    return this.http.get<PageStatsDeviceAndOs>(this.baseUrl + 'pages/' + idPage + '/stats/get-device-and-os-list-by-day', { params });
  }

  getPagesStatsGetClicksByDay(idPage, from, to): Observable<Array<PageStatsClicksByDay>> {
    const params = new HttpParams().set('from', from).set('to', to);
    return this.http.get<Array<PageStatsClicksByDay>>(this.baseUrl + 'pages/' + idPage + '/stats/get-clicks-by-day', { params });
  }

  getFormInteractions(
    idPage,
    pageNum?,
    pageSize?,
    sortColumn?,
    sortDirection?,
    body?: ApiGetFormInteractionsOptions
  ): Observable<FormInteraction> {
    let params = new HttpParams();
    if (pageNum) {
      params = params.append('page', pageNum);
    }
    if (pageSize) {
      params = params.append('size', pageSize);
    }
    if (sortColumn) {
      switch (sortColumn.toUpperCase()) {
        case 'CREATETIME':
          sortColumn = 'CREATE_TIME';
          break;
        case 'PHONENUMBER':
          sortColumn = 'PHONE_NUMBER';
          break;
      }
      params = params.append('sortColumn', sortColumn.toUpperCase());
    } else {
      params = params.append('sortColumn', 'CREATE_TIME');
    }
    if (sortDirection) {
      params = params.append('sortDirection', sortDirection);
    } else {
      params = params.append('sortDirection', 'd');
    }
    // if (returnFormat) {
    // params = params.append('returnFormat', 'FULL');
    // }
    // if (body && body['contacts-filters'].filters && body['contacts-filters'].filters.length) {
    //   delete body['basic-filters'];
    // }

    return this.http
      .post(this.baseUrl + 'pages/form-interactions/' + idPage, body, {
        params
      })
      .pipe(
        map(data => {
          return FormInteractions.deserialize({
            data: (data && (data as any).data ? ((data as any).data as Array<string>) : []).map(data2 =>
              FormInteraction.deserialize(data2)
            ),
            total: (data as any).total
          });
        })
      );
  }

  errorHandler(errorMessage: string, overlayContainer: ToastContainerDirective = null) {
    if (errorMessage.includes('has reached max pages')) {
      return this.atlasToast.error('pages_published_max_reached', null, 6000, overlayContainer);
    } else {
      return this.atlasToast.error('common_error', null, 6000, overlayContainer);
    }
  }
}
